import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useQuranOptions } from "@/app/providers/QuranContextProvider";
import { QuranResponseType, QuranWbwTranslationType } from "../types";
import { useIndexedDB } from "./useIndexedDb";
import { loadWbwTranslation } from "@/actions/load-wbw-translation";
import {
  QURAN_DATABASE_TABLES,
  QURAN_WBW_DATABASE_KEY_NAME,
  QURAN_WBW_DATABASE_NAME,
  QURAN_WBW_DATABASE_TABLES,
  QURAN_WBW_DATABASE_VERSION,
} from "../constants";

const tanslationStatus = new Map<string, Promise<QuranWbwTranslationType[]>>();
const useWbwTranslations = (wordId: number, page: number) => {
  const [wordTranslation, setWorTranslation] = useState<
    QuranWbwTranslationType | undefined
  >(undefined);
  const { data, quranMushaf, wfwLanguage } = useQuranOptions();
  const { saveRecords, getRecord, getAllRecords, isDBConnecting } =
    useIndexedDB<QuranWbwTranslationType>(
      QURAN_WBW_DATABASE_NAME,
      QURAN_WBW_DATABASE_TABLES,
      QURAN_WBW_DATABASE_KEY_NAME,
      QURAN_WBW_DATABASE_VERSION,
    );

  const loadTranslations = useCallback(async () => {
    const trans = await getRecord(wfwLanguage, wordId);

    if (trans) {
      setWorTranslation(trans);
      return;
    }

    let start: number, end: typeof start;
    let pages: QuranResponseType[];

    if (quranMushaf.type == "indopak15" || quranMushaf.type == "indopak16") {
      if (quranMushaf.lines == 15 || quranMushaf.lines == 16) {
        pages = data.current.IndoPak[quranMushaf.lines][page];
      }
    } else {
      pages = data.current.Uthmani[quranMushaf.type][page];
    }

    const wrds = pages!![pages!!.length - 1].words;
    start = pages!![0].words[0].id as number;

    end = pages!![pages!!.length - 1].words[wrds.length - 1].id as number;

    if (tanslationStatus.has(`${wfwLanguage}-${start}:${end}`)) {
      const cachedPromise = tanslationStatus.get(
        `${wfwLanguage}-${start}:${end}`,
      );
      if (cachedPromise) {
        cachedPromise.then(() => {
          const getTrans = async () => {
            const trans = await getRecord(wfwLanguage, wordId);
            setWorTranslation(trans);
          };
          getTrans();
        });
        return;
      }
    }

    try {
      const loadPromise = new Promise<Array<QuranWbwTranslationType>>(
        async (resolve, reject) => {
          const result = await loadWbwTranslation(wfwLanguage, start, end);

          if ("error" in result) reject(result.error);
          else {
            resolve(result);
            return result;
          }
        },
      );

      tanslationStatus.set(`${wfwLanguage}-${start}:${end}`, loadPromise);

      const result = await loadPromise;
      saveRecords(wfwLanguage, result);
      const trans = await getRecord(wfwLanguage, wordId);
      setWorTranslation(trans);
    } catch (e) {
      console.log("Error: ", e);
      setWorTranslation({
        id: 0,
        verse_id: 0,
        word_id: 0,
        translation: "Something went wrong",
        root: "Something went wrong",
      });
    }
  }, [isDBConnecting]);

  useEffect(() => {
    if (isDBConnecting) return;

    loadTranslations();
  }, [isDBConnecting]);

  return { wordTranslation };
};

export default useWbwTranslations;
